import { useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSidebar } from './Sidebar.context';
import {
  NAVIGATION_CONFIG,
  getNavItemTo,
  isGroupActive,
  isNavItemActive,
} from './Sidebar.config';
import { NavItem } from './Sidebar.types';
import { Icon } from '~components/shared/Icon';

import {
  SidebarContainer,
  NavWrapper,
  NavList,
  NavGroupHeader,
  NavGroupLink,
  NavItemLink,
  SubMenu,
  ChevronIcon,
  MobileOverlay,
  MobileCloseButton,
  SidebarHeader,
  Shortcut,
  NavItemWrapper,
  Tooltip,
  FlyoutMenu,
  FlyoutHeader,
  FlyoutItem,
} from './styles';

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

interface NavGroupProps {
  item: NavItem;
  isCollapsed: boolean;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  currentPath: string;
  currentSearch: string;
  isOrange?: boolean;
  nested?: boolean;
}

function NavGroup({
  item,
  isCollapsed,
  isActive,
  isExpanded,
  onToggle,
  onNavigate,
  currentPath,
  currentSearch,
  isOrange = false,
  nested = false,
}: NavGroupProps) {
  const history = useHistory();
  const { toggleGroup, isGroupExpanded } = useSidebar();
  const hasChildren = item.children && item.children.length > 0;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (hasChildren) {
          onToggle();
        }
      }
      if (e.key === 'ArrowRight' && hasChildren && !isExpanded) {
        e.preventDefault();
        onToggle();
      }
      if (e.key === 'ArrowLeft' && hasChildren && isExpanded) {
        e.preventDefault();
        onToggle();
      }
    },
    [hasChildren, isExpanded, onToggle]
  );

  const handleCollapsedClick = useCallback(() => {
    if (isCollapsed && item.path) {
      history.push(getNavItemTo(item));
      onNavigate();
    } else if (!isCollapsed && hasChildren) {
      onToggle();
    }
  }, [isCollapsed, item, history, onNavigate, hasChildren, onToggle]);

  if (hasChildren) {
    return (
      <NavItemWrapper $collapsed={isCollapsed} role="none">
        <NavGroupHeader
          $active={isActive}
          $expanded={isExpanded}
          $collapsed={isCollapsed}
          $isOrange={isOrange}
          $nested={nested}
          onClick={handleCollapsedClick}
          onKeyDown={handleKeyDown}
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={isExpanded}
          aria-label={item.label}
        >
          <Icon name={item.icon} size="md" />
          {!isCollapsed && (
            <>
              <p>{item.label}</p>
              {item.shortcut && <Shortcut>{item.shortcut}</Shortcut>}
              <ChevronIcon $expanded={isExpanded} $collapsed={isCollapsed}>
                <ChevronDown />
              </ChevronIcon>
            </>
          )}
        </NavGroupHeader>

        {isCollapsed && hasChildren && (
          <FlyoutMenu $visible={true} data-flyout>
            <FlyoutHeader $isOrange={isOrange}>
              <Icon name={item.icon} size="sm" />
              {item.label}
            </FlyoutHeader>
            {item.children!.map(child => {
              const siblings = item.children!;
              const hasNestedChildren = child.children && child.children.length > 0;

              if (hasNestedChildren) {
                return (
                  <div key={child.id}>
                    {child.path && (
                      <FlyoutItem
                        to={getNavItemTo(child)}
                        $active={isNavItemActive(child, currentPath, currentSearch)}
                        onClick={onNavigate}
                      >
                        <Icon name={child.icon} size="sm" />
                        <p>{child.label}</p>
                      </FlyoutItem>
                    )}
                    {child.children!.map(nestedChild => (
                      <FlyoutItem
                        key={nestedChild.id}
                        to={nestedChild.path ? getNavItemTo(nestedChild) : '#'}
                        $active={nestedChild.path ? isNavItemActive(nestedChild, currentPath, currentSearch) : false}
                        onClick={onNavigate}
                        style={{ paddingLeft: '2.5rem' }}
                      >
                        <Icon name={nestedChild.icon} size="sm" />
                        <p>{nestedChild.label}</p>
                      </FlyoutItem>
                    ))}
                  </div>
                );
              }

              return (
                <FlyoutItem
                  key={child.id}
                  to={child.path ? getNavItemTo(child) : '#'}
                  $active={child.path ? isNavItemActive(child, currentPath, currentSearch) : false}
                  onClick={onNavigate}
                >
                  <Icon name={child.icon} size="sm" />
                  <p>{child.label}</p>
                </FlyoutItem>
              );
            })}
          </FlyoutMenu>
        )}

        {isExpanded && !isCollapsed && (
          <SubMenu $collapsed={isCollapsed} role="menu" aria-label={`Submenu de ${item.label}`}>
            {item.children!.map(child => {
              const siblings = item.children!;
              const hasNestedChildren = child.children && child.children.length > 0;

              if (hasNestedChildren) {
                return (
                  <NavGroup
                    key={child.id}
                    item={child}
                    isCollapsed={false}
                    isActive={isGroupActive(child, currentPath, currentSearch)}
                    isExpanded={isGroupExpanded(child.id)}
                    onToggle={() => toggleGroup(child.id)}
                    onNavigate={onNavigate}
                    currentPath={currentPath}
                    currentSearch={currentSearch}
                    nested={true}
                  />
                );
              }

              return (
                <li key={child.id} role="none">
                  <NavItemLink
                    to={child.path ? getNavItemTo(child) : '#'}
                    $active={child.path ? isNavItemActive(child, currentPath, currentSearch) : false}
                    $collapsed={isCollapsed}
                    $nestedChild={nested}
                    role="menuitem"
                    aria-current={child.path && isNavItemActive(child, currentPath, currentSearch) ? 'page' : undefined}
                    onClick={onNavigate}
                  >
                    <Icon name={child.icon} size="sm" />
                    <p>{child.label}</p>
                  </NavItemLink>
                </li>
              );
            })}
          </SubMenu>
        )}
      </NavItemWrapper>
    );
  }

  return (
    <NavItemWrapper $collapsed={isCollapsed} role="none">
      <NavGroupLink
        to={item.path || '#'}
        $active={isActive}
        $expanded={false}
        $collapsed={isCollapsed}
        $isOrange={isOrange}
        $nested={nested}
        role="menuitem"
        aria-current={isActive ? 'page' : undefined}
        onClick={onNavigate}
      >
        <Icon name={item.icon} size="md" />
        {!isCollapsed && (
          <>
            <p>{item.label}</p>
            {item.shortcut && <Shortcut>{item.shortcut}</Shortcut>}
          </>
        )}
      </NavGroupLink>

      {isCollapsed && (
        <Tooltip $collapsed={isCollapsed} data-tooltip>{item.label}</Tooltip>
      )}
    </NavItemWrapper>
  );
}

export function Sidebar() {
  const { pathname, search } = useLocation();
  const {
    isCollapsed,
    isMobileOpen,
    closeMobile,
    toggleGroup,
    isGroupExpanded,
  } = useSidebar();

  // Removido: auto-expansão de grupos - todos os menus iniciam retraídos
  // useEffect(() => {
  //   NAVIGATION_CONFIG.forEach(item => {
  //     if (item.children && isGroupActive(item, pathname) && !isGroupExpanded(item.id)) {
  //       toggleGroup(item.id);
  //     }
  //   });
  // }, [pathname]);

  const orangeItems = ['dashboard', 'support', 'virtual-manager'];

  return (
    <>
      <MobileOverlay $isOpen={isMobileOpen} onClick={closeMobile} />
      <SidebarContainer
        $collapsed={isCollapsed}
        $mobileOpen={isMobileOpen}
        role="navigation"
        aria-label="Menu principal"
      >
        <SidebarHeader>
          <MobileCloseButton onClick={closeMobile} aria-label="Fechar menu">
            <CloseIcon />
          </MobileCloseButton>
        </SidebarHeader>

        <NavWrapper>
          <NavList role="menubar" aria-label="Navegacao principal">
            {NAVIGATION_CONFIG.map(item => (
              <NavGroup
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                isActive={isGroupActive(item, pathname, search)}
                isExpanded={isGroupExpanded(item.id)}
                onToggle={() => toggleGroup(item.id)}
                onNavigate={closeMobile}
                currentPath={pathname}
                currentSearch={search}
                isOrange={orangeItems.includes(item.id)}
              />
            ))}
          </NavList>
        </NavWrapper>
      </SidebarContainer>
    </>
  );
}

export { Menu } from '~components/Menu';
